/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 *
 * Created by Claude Code 2026-09-21: Phase 4 Sidebar Anchors
 * Individual app renderer for sidebar during presentations
 */

import { Box, Text, Image, useColorModeValue } from '@chakra-ui/react';
import { useMemo, useState, useEffect } from 'react';

import type { App } from '@sage3/applications/schema';
import { Applications } from '@sage3/applications/apps';
import { useAssetStore, apiUrls, isUUIDv4 } from '@sage3/frontend';
import type { ExtraImageType, ExtraPDFType } from '@sage3/shared/types';

interface SidebarAppProps {
  app: App;
  position: { x: number; y: number }; // Position in sidebar (viewport pixels)
  size: { width: number; height: number }; // Size in sidebar (viewport pixels)
}

/**
 * Renders an individual app in the sidebar
 *
 * Features:
 * - Positioned and sized according to layout algorithm
 * - Fully interactive (clicks, typing, etc.)
 * - Scaled to fit sidebar dimensions
 * - Maintains aspect ratio when possible
 */
export function SidebarApp({ app, position, size }: SidebarAppProps) {
  // Get app component
  const hasType = app.data.type in Applications;
  const AppComponent = hasType ? Applications[app.data.type].AppComponent : null;

  // Calculate scale factor to fit app into allocated space
  const scale = useMemo(() => {
    const scaleX = size.width / app.data.size.width;
    const scaleY = size.height / app.data.size.height;
    // Use smaller scale to maintain aspect ratio
    return Math.min(scaleX, scaleY);
  }, [size, app.data.size]);

  // Simplified preview renderer for Stickie apps
  const renderStickiePreview = () => {
    const state = app.data.state as any;
    const text = state.text || '';
    const color = state.color || 'yellow';
    const fontSize = Math.max(8, (state.fontSize || 24) * scale);

    const bgColor = useColorModeValue(`${color}.200`, `${color}.200`);

    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        color="black"
        p={2}
        overflow="hidden"
        fontSize={`${fontSize}px`}
        fontFamily="Arial"
        whiteSpace="pre-wrap"
        borderRadius="md"
      >
        {text || <Text opacity={0.5} fontSize={fontSize}>Type here...</Text>}
      </Box>
    );
  };

  // Clock preview - simple digital display
  const ClockPreview = () => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
      const timer = setInterval(() => setTime(new Date()), 1000);
      return () => clearInterval(timer);
    }, []);

    const state = app.data.state as any;
    const use24Hour = state.use24Hour ?? false;
    const timeStr = time.toLocaleTimeString('en-US', {
      hour12: !use24Hour,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    return (
      <Box
        w="100%"
        h="100%"
        bg="gray.900"
        _dark={{ bg: 'gray.900' }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        color="green.400"
        fontFamily="monospace"
      >
        <Text fontSize={Math.max(12, 20 * scale)} fontWeight="bold">
          {timeStr}
        </Text>
        <Text fontSize={Math.max(8, 12 * scale)} opacity={0.7} mt={1}>
          {time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </Text>
      </Box>
    );
  };

  // ImageViewer preview - display the image
  const ImageViewerPreview = () => {
    const state = app.data.state as any;
    const assets = useAssetStore((state) => state.assets);
    const [imageUrl, setImageUrl] = useState<string>('');

    // Memoize asset lookup to prevent infinite re-renders
    const asset = useMemo(() => {
      const assetId = state.assetid;
      if (!assetId || !isUUIDv4(assetId)) return null;
      return assets.find((a) => a._id === assetId);
    }, [assets, state.assetid]);

    useEffect(() => {
      const assetId = state.assetid;
      if (!assetId) return;

      // Check if it's a UUID (asset reference) or direct URL
      if (isUUIDv4(assetId)) {
        if (asset) {
          const extra = asset.data.derived as ExtraImageType;
          if (extra && extra.sizes && extra.sizes.length > 0) {
            // Use smallest resolution for sidebar
            const smallest = extra.sizes.reduce((p, v) => p.width < v.width ? p : v);
            setImageUrl(smallest.url);
          } else {
            // Use original file
            setImageUrl(apiUrls.assets.getAssetById(asset.data.file));
          }
        }
      } else {
        // Direct URL
        setImageUrl(assetId);
      }
    }, [asset, state.assetid]);

    const bgColor = useColorModeValue('white', 'gray.700');

    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
      >
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={app.data.title}
            objectFit="contain"
            w="100%"
            h="100%"
          />
        ) : (
          <Text fontSize="xs" opacity={0.6}>
            Loading...
          </Text>
        )}
      </Box>
    );
  };

  // PDFViewer preview - display current page
  const PDFViewerPreview = () => {
    const state = app.data.state as any;
    const assets = useAssetStore((state) => state.assets);
    const [pageUrl, setPageUrl] = useState<string>('');
    const [pageInfo, setPageInfo] = useState<string>('');

    // Memoize asset lookup to prevent infinite re-renders
    const asset = useMemo(() => {
      return assets.find((a) => a._id === state.assetid);
    }, [assets, state.assetid]);

    useEffect(() => {
      if (!asset?.data?.derived) return;

      // Cast to any to handle the ExtraPDFType structure
      const pages = asset.data.derived as any;

      if (Array.isArray(pages) && pages.length > 0) {
        // Get current page (default to page 0)
        const currentPage = state.currentPage || 0;
        const pageIndex = Math.min(currentPage, pages.length - 1);

        // PDF pages are nested arrays: pages[pageIndex] returns an array of resolutions
        // Get the first resolution for sidebar display
        const pageData = pages[pageIndex];
        if (pageData) {
          // Each page has multiple resolutions - use first one (smallest)
          const pageResolutions = Array.isArray(pageData) ? pageData : [pageData];
          const firstResolution = pageResolutions[0];

          if (firstResolution && typeof firstResolution === 'object' && 'url' in firstResolution) {
            setPageUrl(firstResolution.url as string);
            setPageInfo(`Page ${pageIndex + 1} of ${pages.length}`);
          } else if (typeof firstResolution === 'string') {
            setPageUrl(firstResolution);
            setPageInfo(`Page ${pageIndex + 1} of ${pages.length}`);
          }
        }
      }
    }, [asset, state.currentPage]);

    const bgColor = useColorModeValue('white', 'gray.700');

    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        position="relative"
      >
        {pageUrl ? (
          <>
            <Image
              src={pageUrl}
              alt={`PDF page ${state.currentPage + 1}`}
              objectFit="contain"
              w="100%"
              h="100%"
            />
            {/* Page number overlay */}
            <Box
              position="absolute"
              bottom={1}
              right={1}
              bg="blackAlpha.700"
              color="white"
              px={2}
              py={0.5}
              borderRadius="sm"
              fontSize="xs"
            >
              {pageInfo}
            </Box>
          </>
        ) : (
          <Text fontSize="xs" opacity={0.6}>
            Loading PDF...
          </Text>
        )}
      </Box>
    );
  };

  // Generic placeholder for unsupported types
  const GenericPlaceholder = () => {
    const bgColor = useColorModeValue('gray.100', 'gray.800');
    const textColor = useColorModeValue('gray.700', 'gray.300');

    return (
      <Box
        w="100%"
        h="100%"
        bg={bgColor}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        p={2}
      >
        <Text fontSize="sm" fontWeight="bold" textAlign="center" color={textColor}>
          {app.data.title}
        </Text>
        <Text fontSize="xs" opacity={0.6} mt={1} color={textColor}>
          {app.data.type}
        </Text>
      </Box>
    );
  };

  // Choose renderer based on app type
  const renderPreview = () => {
    switch (app.data.type) {
      case 'Stickie':
        return renderStickiePreview();
      case 'Clock':
        return <ClockPreview />;
      case 'ImageViewer':
        return <ImageViewerPreview />;
      case 'PDFViewer':
        return <PDFViewerPreview />;
      default:
        return <GenericPlaceholder />;
    }
  };

  return (
    <Box
      position="absolute"
      left={`${position.x}px`}
      top={`${position.y}px`}
      width={`${size.width}px`}
      height={`${size.height}px`}
      overflow="hidden"
      borderRadius="md"
      boxShadow="md"
    >
      {renderPreview()}
    </Box>
  );
}
